import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import { Box } from '@sendible/design-system';

export const Container = styled.div<{ $isDisabled?: boolean; $showCheckboxAlways?: boolean }>`
  ${({ theme: { colors, motionSpeeds, motions }, $isDisabled, $showCheckboxAlways }) => css`
    align-items: flex-start;
    background: ${colors.brandLight};
    box-shadow: 0 0 0 1px ${colors.brandDarkAlpha10};
    border-radius: 0.5rem;
    box-sizing: border-box;
    display: flex;
    flex: 1;
    overflow: visible;
    padding: 0.75rem 1rem;
    position: relative;
    transition: background 0.15s ease;

    &:hover {
      .list-card-actions {
        opacity: 1;
        visibility: visible;
      }

      .ds-checkbox {
        margin-right: 0.25rem;
        opacity: 1;
        width: 1.5rem;
      }
    }

    &:focus-within .ds-checkbox {
      margin-right: 0.25rem;
      opacity: 1;
      width: 1.5rem;
    }

    .ds-checkbox {
      margin-right: 0;
      opacity: ${$showCheckboxAlways ? 1 : 0};
      transition:
        margin-right ${motionSpeeds.fast} ${motions.default},
        opacity ${motionSpeeds.fast} ${motions.default},
        width ${motionSpeeds.fast} ${motions.default};
      width: ${$showCheckboxAlways ? '1.5rem' : 0};
    }

    ${
      $isDisabled &&
      css`
        cursor: not-allowed;
        opacity: 0.7;

        * {
          cursor: not-allowed;
        }
      `
    }
  `}
`;

export const StatusIconWrapper = styled.div`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  z-index: 0;

  .status-icon {
    align-items: center;
    display: inline-flex;
    flex-shrink: 0;
    height: 1rem;
    justify-content: center;
    position: relative;
    width: 1rem;
  }

  .icon,
  .spinner {
    height: 1rem;
    width: 1rem;
  }

  .ds-checkbox {
    flex-shrink: 0;
    overflow: hidden;
  }
`;

export const InfoArea = styled.div`
  ${({ theme: { colors, fontSizes } }) => css`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
    min-width: 6rem;
    /* padding: 0.75rem 0.5rem 0.75rem 0.75rem; */
    position: relative;
    text-transform: uppercase;

    p {
      color: ${colors.brandDarkTint30};
      font-size: ${fontSizes.s12};
      line-height: 1em;
      margin: 0;
      white-space: nowrap;
    }

    .ds-tooltip {
      display: flex;
    }
  `}
`;

export const ContentArea = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export const TopRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
`;

export const ChannelList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
`;

export const ChannelAvatar = styled.div`
  ${() => css`
    align-items: center;
    border-radius: 0.25rem;
    display: flex;
    height: 1.5rem;
    justify-content: center;
    position: relative;
    width: 1.5rem;

    .icon {
      height: 1.5rem;
      padding: 0;
      width: 1.5rem;
    }
  `}
`;

export const ChannelItem = styled.div`
  ${({ theme: { colors, fontSizes } }) => css`
    align-items: center;
    background: ${colors.brandDarkTint95};
    border-radius: 0.25rem;
    display: flex;
    gap: 0.5rem;
    min-width: 0;
    padding: 0.25rem 0.625rem 0.25rem 0.25rem;

    .text {
      color: ${colors.brandDark};
      font-size: ${fontSizes.s12};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `}
`;

export const WorkspaceAvatar = styled.div<{ $color: string; $isDark: boolean }>`
  ${({ theme: { colors, fontWeights }, $color, $isDark }) => css`
    align-items: center;
    background: ${$color} !important;
    border-radius: 1.25rem;
    display: flex;
    flex-shrink: 0;
    height: 1.25rem;
    justify-content: center;
    position: relative;
    width: 1.25rem;

    &::before {
      border-radius: inherit;
      box-shadow: inset 0 0 0 1px ${colors.brandDarkAlpha20} !important;
      content: '';
      display: block;
      height: 100%;
      left: 0;
      pointer-events: none;
      position: absolute;
      top: 0;
      user-select: none;
      width: 100%;
      z-index: 1;
    }

    span {
      color: ${$isDark ? colors.brandLightAlpha90 : colors.brandDarkAlpha80} !important;
      display: inline-block;
      font-size: 0.625rem;
      font-weight: ${fontWeights.semiBold};
      line-height: 1em;
    }
  `}
`;

export const MessageText = styled.p`
  ${({ theme: { colors, fontSizes } }) => css`
    color: ${colors.brandDark};
    display: -webkit-box;
    font-size: ${fontSizes.s14};
    line-height: 1.4em;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;

    a {
      color: ${colors.brandPrimary};
      text-decoration: none;
      word-break: break-all;

      &:hover {
        text-decoration: underline;
      }
    }
  `}
`;

export const MediaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const MediaThumb = styled.div`
  ${() => css`
    border-radius: 0.5rem;
    cursor: pointer;
    flex-shrink: 0;
    height: 5rem;
    overflow: hidden;
    position: relative;
    width: 5rem;

    img,
    canvas {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }

    .skeleton {
      border-radius: 0;
    }

    &:hover {
      opacity: 0.85;
    }
  `}
`;

export const ThumbHighlight = styled.div`
  ${({ theme: { colors, fontWeights } }) => css`
    align-items: flex-end;
    background: ${`linear-gradient(0deg, ${colors.brandDarkAlpha70} 0%, rgba(0, 0, 0, 0) 100%)`};
    bottom: 0;
    display: flex;
    height: 100%;
    justify-content: flex-end;
    left: 0;
    padding: 0.5rem;
    position: absolute;
    width: 100%;
    z-index: 1;

    .icon {
      height: 1rem;
      width: 1rem;
    }

    span {
      color: ${colors.brandLight};
      font-size: 0.5rem !important;
      font-weight: ${fontWeights.medium};
      line-height: 1.3em;
      margin: 0;
      padding: 0;
    }
  `}
`;

export const ThumbOverflow = styled.div`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    align-items: center;
    background: ${colors.brandDarkAlpha70};
    bottom: 0;
    color: ${colors.brandLight};
    display: flex;
    font-size: ${fontSizes.s20};
    font-weight: ${fontWeights.medium};
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;
  `}
`;

export const FailedRecipientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ActionsArea = styled.div`
  ${({ theme: { colors } }) => css`
    align-items: center;
    background: ${rgba(colors.brandLight, 0.95)};
    border-radius: 0.5rem;
    box-shadow:
      0 1px 3px 0 ${colors.brandDarkAlpha30},
      0 1px 1px 0 ${colors.brandDarkTint90},
      0 2px 1px -1px ${colors.brandDarkAlpha20};
    display: flex;
    flex-shrink: 0;
    gap: 0.125rem;
    opacity: 0;
    padding: 0.25rem;
    position: absolute;
    right: 2.75rem;
    top: 0.75rem;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
    visibility: hidden;
  `}
`;

export const DeleteActions = styled(Box)`
  gap: 0.5rem;
  margin-top: 1rem;
  width: 100%;

  .button {
    flex: 1;
  }
`;

export const ErrorBox = styled.div`
  ${({ theme: { colors, fontSizes } }) => css`
    background: ${colors.semanticDangerTint90};
    border: 1px solid ${colors.semanticDangerTint80};
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;

    p {
      color: ${colors.semanticDangerShade20};
      font-size: ${fontSizes.s12};
      margin: 0 !important;
    }
  `}
`;

export const CampaignsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

export const CampaignTag = styled.span<{ $color: string; $isDark: boolean }>`
  ${({ theme: { colors, fontSizes, fontWeights }, $color, $isDark }) => css`
    align-items: center;
    display: inline-flex;
    font-size: ${fontSizes.s12};
    gap: 0.375rem;

    .campaign-tag-avatar {
      align-items: center;
      background: ${$color};
      border-radius: 50%;
      color: ${$isDark ? colors.brandLightAlpha90 : colors.brandDarkAlpha80};
      display: inline-flex;
      flex-shrink: 0;
      font-size: 0.5rem;
      font-weight: ${fontWeights.semiBold};
      height: 1rem;
      justify-content: center;
      width: 1rem;
    }
  `}
`;
